import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowUpRight, Loader2, CheckCircle2, Phone, Calendar } from "lucide-react";
import { toast } from "sonner";
import { fadeUp, stagger } from "../lib/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GOALS = ["More leads", "Better website", "Paid ads", "Local SEO", "Not sure yet"];

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", business: "", goal: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | loading | success

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    setStatus("loading");
    try {
      await axios.post(`${API}/leads`, form);
      setStatus("success");
      toast.success("Booked! We'll be in touch within one business day.");
    } catch (err) {
      setStatus("idle");
      toast.error("Something went wrong. Please try again.");
    }
  };

  return (
    <section id="contact" className="relative py-28 lg:py-36 panel-grid overflow-hidden" data-testid="contact-section">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 relative z-10">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
          variants={stagger}
          className="lg:col-span-5"
        >
          <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.3em] text-[#C6FF00] mb-5">
            / Let's Launch
          </motion.p>
          <motion.h2 variants={fadeUp} className="text-5xl lg:text-7xl font-black uppercase tracking-tighter leading-[0.9] mb-8">
            Book your<br />strategy<br /><span className="text-[#C6FF00]">call.</span>
          </motion.h2>
          <motion.p variants={fadeUp} className="text-lg text-[#8A939B] font-medium leading-relaxed max-w-md mb-10">
            Tell us where you want to grow. We'll map the systems to get you there —
            no obligation, no fluff. And no camera needed — a simple phone call is all it takes.
          </motion.p>
          <motion.div variants={fadeUp} className="space-y-4">
            <a href="/book" className="flex items-center gap-3 text-white hover:text-[#C6FF00] transition-colors" data-testid="contact-scheduler-link">
              <span className="flex h-11 w-11 items-center justify-center border border-[#2A2D34]"><Calendar className="h-5 w-5" /></span>
              <span className="font-semibold">Prefer to self-schedule? Pick a slot →</span>
            </a>
            <a href="mailto:hello@hughlaunch.com" className="flex items-center gap-3 text-white hover:text-[#C6FF00] transition-colors" data-testid="contact-email-link">
              <span className="flex h-11 w-11 items-center justify-center border border-[#2A2D34]"><Phone className="h-5 w-5" /></span>
              <span className="font-semibold">hello@hughlaunch.com</span>
            </a>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-7"
        >
          {status === "success" ? (
            <div className="h-full min-h-[420px] flex flex-col items-center justify-center border border-[#C6FF00]/40 bg-[#0A0C0F] p-12 text-center" data-testid="contact-success">
              <CheckCircle2 className="h-16 w-16 text-[#C6FF00] mb-6" />
              <h3 className="text-3xl font-black uppercase tracking-tight mb-3">You're on the list.</h3>
              <p className="text-[#8A939B] font-medium max-w-sm mb-8">
                Thanks {form.name.split(" ")[0]} — we've got your details. Want to move faster? Lock in your strategy call right now.
              </p>
              <a
                href="/book"
                data-testid="contact-success-scheduler"
                className="btn-shine group flex items-center justify-center gap-2 rounded-full bg-[#C6FF00] text-[#0A0C0F] font-bold uppercase text-sm tracking-wider px-8 py-4 hover:bg-white transition-colors duration-300"
              >
                <Calendar className="h-4 w-4" /> Book Your Call Now
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.5} />
              </a>
            </div>
          ) : (
            <form onSubmit={submit} className="border border-[#2A2D34] bg-[#0A0C0F] p-8 lg:p-10 space-y-6" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-6">
                <Field label="Name *" value={form.name} onChange={update("name")} testid="lead-name" />
                <Field label="Email *" type="email" value={form.email} onChange={update("email")} testid="lead-email" />
              </div>
              <Field label="Business name" value={form.business} onChange={update("business")} testid="lead-business" />
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-[#8A939B] mb-3">Primary goal</label>
                <div className="flex flex-wrap gap-3">
                  {GOALS.map((g) => (
                    <button
                      type="button"
                      key={g}
                      onClick={() => setForm((f) => ({ ...f, goal: g }))}
                      data-testid={`goal-${g.replace(/\s+/g, "-").toLowerCase()}`}
                      className={`px-4 py-2.5 text-sm font-semibold uppercase tracking-wide border transition-colors duration-200 ${
                        form.goal === g
                          ? "bg-[#C6FF00] text-[#0A0C0F] border-[#C6FF00]"
                          : "border-[#2A2D34] text-[#8A939B] hover:border-[#C6FF00] hover:text-white"
                      }`}
                    >
                      {g}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-[#8A939B] mb-3">Anything else?</label>
                <textarea
                  value={form.message}
                  onChange={update("message")}
                  rows={4}
                  data-testid="lead-message"
                  className="w-full bg-[#111317] border border-[#2A2D34] px-4 py-3 text-white font-medium focus:border-[#C6FF00] focus:outline-none focus:ring-1 focus:ring-[#C6FF00] transition-colors resize-none"
                  placeholder="Tell us about your business..."
                />
              </div>
              <button
                type="submit"
                disabled={status === "loading"}
                data-testid="lead-submit"
                className="btn-shine group w-full flex items-center justify-center gap-2 rounded-full bg-white text-[#0A0C0F] font-bold uppercase text-sm tracking-wider px-8 py-5 hover:bg-[#C6FF00] transition-colors duration-300 disabled:opacity-60"
              >
                {status === "loading" ? (
                  <><Loader2 className="h-5 w-5 animate-spin" /> Sending...</>
                ) : (
                  <>Audit My Brand <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.5} /></>
                )}
              </button>

              <div className="flex items-center gap-4">
                <span className="h-px flex-1 bg-[#2A2D34]" />
                <span className="text-xs font-semibold uppercase tracking-wider text-[#6B7280]">or</span>
                <span className="h-px flex-1 bg-[#2A2D34]" />
              </div>

              <a
                href="/book"
                data-testid="lead-book-call"
                className="group w-full flex items-center justify-center gap-2 rounded-full border border-white bg-white text-[#0A0C0F] font-bold uppercase text-sm tracking-wider px-8 py-5 hover:bg-[#C6FF00] hover:border-[#C6FF00] transition-colors duration-300"
              >
                <Calendar className="h-4 w-4" /> Book My Strategy Call
              </a>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};

const Field = ({ label, type = "text", value, onChange, testid }) => (
  <div>
    <label className="block text-xs font-bold uppercase tracking-wider text-[#8A939B] mb-3">{label}</label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      data-testid={testid}
      className="w-full bg-[#111317] border border-[#2A2D34] px-4 py-3.5 text-white font-medium focus:border-[#C6FF00] focus:outline-none focus:ring-1 focus:ring-[#C6FF00] transition-colors"
    />
  </div>
);
